import { useNavigate } from 'react-router-dom';
import { Camera, Sparkles, Droplets, Bell, Stethoscope } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { trackEvent } from '@/lib/gtag';

export function OnboardingHero() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const firstName = (() => {
    const meta = user?.user_metadata as Record<string, unknown> | undefined;
    const full = (meta?.full_name as string) || (meta?.name as string) || user?.email || '';
    return full.split(' ')[0]?.split('@')[0] || '';
  })();

  const benefits = [
    { icon: Camera, text: 'Identificação científica em segundos' },
    { icon: Droplets, text: 'Cronograma de rega personalizado' },
    { icon: Bell, text: 'Lembretes para nunca esquecer' },
    { icon: Stethoscope, text: 'Diagnóstico de saúde quando precisar' },
  ];

  const handleCTA = () => {
    trackEvent('first_plant_cta_clicked');
    navigate('/identify');
  };

  return (
    <section className="px-4 pt-6 pb-4">
      <div className="max-w-lg mx-auto">
        <div className="relative overflow-hidden rounded-2xl gradient-nature text-primary-foreground p-5 sm:p-6 mb-4">
          <div className="absolute -top-8 -right-8 w-32 h-32 bg-white/10 rounded-full blur-2xl" />
          <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-white/10 rounded-full blur-2xl" />
          <div className="relative">
            <div className="inline-flex items-center gap-1.5 bg-white/20 px-2.5 py-1 rounded-full text-xs font-medium mb-3">
              <Sparkles className="h-3 w-3" />
              Bem-vindo{firstName ? `, ${firstName}` : ''}!
            </div>
            <h1 className="text-xl sm:text-2xl font-bold font-display leading-tight mb-2">
              Vamos cuidar da sua primeira planta? 🌱
            </h1>
            <p className="text-sm opacity-90">
              Tire uma foto e o VerdeIA cuida do resto.
            </p>
          </div>
        </div>

        <div className="space-y-2.5 mb-5">
          {benefits.map((b, i) => (
            <div
              key={i}
              className="flex items-center gap-3 p-3 rounded-xl bg-card border border-border animate-fade-in"
              style={{ animationDelay: `${i * 80}ms` }}
            >
              <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <b.icon className="h-4.5 w-4.5 text-primary" />
              </div>
              <span className="text-sm text-foreground">{b.text}</span>
            </div>
          ))}
        </div>

        <Button
          size="lg"
          onClick={handleCTA}
          className="w-full h-14 gradient-nature text-primary-foreground press-scale touch-target text-base font-semibold shadow-lg"
        >
          <Camera className="mr-2 h-5 w-5" />
          Identificar minha primeira planta
        </Button>

        <p className="text-center text-xs text-muted-foreground mt-3">
          Grátis • 3 identificações por mês
        </p>
      </div>
    </section>
  );
}
